import { Component } from 'solid-js';
import { Line } from 'solid-chartjs';
import { Chart, Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement, Filler } from 'chart.js';

// Register Chart.js components
Chart.register(Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement, Filler);

interface LineChartProps {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    borderColor?: string;
    backgroundColor?: string;
    fill?: boolean;
    yAxisID?: string;
    tension?: number;
    borderWidth?: number;
  }[];
  title?: string;
  height?: number;
}

/**
 * LineChart Component
 * Wrapper around Chart.js Line chart for SolidJS
 */
const LineChart: Component<LineChartProps> = (props) => {
  const chartData = () => ({
    labels: props.labels,
    datasets: props.datasets.map((dataset) => ({
      ...dataset,
      borderColor: dataset.borderColor || 'rgb(99, 102, 241)',
      backgroundColor: dataset.backgroundColor || 'rgba(99, 102, 241, 0.1)',
      fill: dataset.fill !== undefined ? dataset.fill : true,
      tension: dataset.tension ?? 0.4,
      borderWidth: dataset.borderWidth ?? 2,
      pointRadius: 3,
      pointHoverRadius: 5,
      yAxisID: dataset.yAxisID || 'y',
    })),
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          color: 'rgb(156, 163, 175)',
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: !!props.title,
        text: props.title,
        color: 'rgb(156, 163, 175)',
        font: {
          size: 14,
          weight: 'normal' as const,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        titleColor: 'rgb(243, 244, 246)',
        bodyColor: 'rgb(209, 213, 219)',
        borderColor: 'rgb(75, 85, 99)',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        boxWidth: 10,
        boxHeight: 10,
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        position: 'left' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Clicks',
          color: 'rgb(156, 163, 175)',
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          font: {
            size: 11,
          },
        },
      },
      y1: {
        type: 'linear' as const,
        position: 'right' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Conversions',
          color: 'rgb(156, 163, 175)',
        },
        grid: {
          display: false,
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          font: {
            size: 11,
          },
        },
      },
      y2: {
        type: 'linear' as const,
        position: 'right' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Earnings ($)',
          color: 'rgb(156, 163, 175)',
        },
        grid: {
          display: false,
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          font: {
            size: 11,
          },
        },
        offset: true,
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          font: {
            size: 11,
          },
        },
      },
    },
  };

  return (
    <div style={{ height: `${props.height || 300}px` }}>
      <Line data={chartData()} options={chartOptions} />
    </div>
  );
};

export default LineChart;
